<script setup>
    import { useRouteQuery } from '@vueuse/router';
    import { useMediaQuery } from '@vueuse/core';
    import { scrollIntoView } from '@/composables/helpers/client-only/scroll';

    import SpeakersGridCard from '~/components/blocks/two-six/speakers/grid-card.vue';

    const isTablet = useMediaQuery('(min-width: 768px)', { ssrWidth: 768 });
    const { $bus, $clientPosthog } = useNuxtApp();

    const props = defineProps({
        headline: {
            type: String,
            default: 'Speakers',
        },
        copy: {
            type: String,
            default: '',
        },
        year: {
            type: Number,
            default: 2026,
        },
        treatAsHero: {
            type: Boolean,
            default: true,
        },
    });

    // ONE REQUEST, DELIBERATELY. 2026-09-16: the grid paged at 20 and derived
    // both its stop condition (`pages`) and each slice offset from the `total`
    // the LAST response happened to carry. /api/v2/speakers is cached per query
    // with no shared version, so its entries drift apart — live production was
    // simultaneously answering 302, 336 and 351 for the same catalog. Pages
    // sliced against different totals overlap, so the grid rendered 514
    // duplicate cards, reached only 260 of 351 speakers, and stopped in the Ns
    // with O-Z unreachable.
    //
    // A single request cannot disagree with itself. 351 speakers is one page at
    // this size, so there is no cross-request arithmetic left to get wrong, and
    // the infinite-scroll path below stays intact for any catalog that does
    // outgrow it — it just never fires at current volumes.
    //
    // The durable fix is to stop trusting `total`/`pages` across requests (page
    // until a response returns fewer than per_page) and to put this endpoint
    // behind the version-aware cache wrapper so a purge reaches it. Both are
    // bigger than an event day allows.
    const PER_PAGE = 400;

    const page = useRouteQuery('page', 1);
    // Mirror the agenda's session filters: Session Type + Theme, in their own
    // URL params so the speaker grid filters the same way the sessions do.
    const sessionTypes = useRouteQuery('session_types', []);
    const themes = useRouteQuery('themes', []);
    const loadingPage = ref(parseInt(page.value, 10) || 1);
    const controls = ref(null);
    const showTags = ref(false);
    const showSearch = ref(false);

    const toArr = (v) => Array.isArray(v) ? v : (v ? String(v).split(',').filter(Boolean) : []);
    const selectedSessionTypes = computed(() => toArr(sessionTypes.value));
    const selectedThemes = computed(() => toArr(themes.value));
    // Combined ids drive the chips + the speaker API's filter_tags (which ORs a
    // speaker in if any of their sessions carries one of these tags).
    const activeFilterIds = computed(() => [...selectedSessionTypes.value, ...selectedThemes.value]);
    const activeFilterCount = computed(() => activeFilterIds.value.length);

    const removeFilter = (id) => {
        sessionTypes.value = selectedSessionTypes.value.filter(t => t !== id);
        themes.value = selectedThemes.value.filter(t => t !== id);
    };
    const clearFilters = () => {
        sessionTypes.value = [];
        themes.value = [];
    };

    const params = computed(() => ({
        year: props.year || null,
        page: loadingPage.value,
        per_page: PER_PAGE,
        filter_tags: activeFilterIds.value.length ? activeFilterIds.value.join(',') : null,
    }));

    const { data, pending, refresh } = useLazyAsyncData(
        `speakers-index-${props.year}`,
        () => $fetch('/api/v2/speakers', { params: { ...params.value } }),
        { watch: [loadingPage, sessionTypes, themes] },
    );

    const pageBuckets = ref({});
    const loadingMore = ref(false);

    watch(() => data.value?.speakers, (newSpeakers) => {
        if (!newSpeakers) {
            loadingMore.value = false;
            return;
        }
        const cp = parseInt(data.value?.current_page || loadingPage.value, 10) || 1;
        pageBuckets.value = {
            ...pageBuckets.value,
            [cp]: newSpeakers.map(s => ({ ...s, _page: cp })),
        };
        loadingMore.value = false;
        // IntersectionObserver only fires on state transitions; if the sentinel
        // was already in view when new cards rendered, re-trigger by checking
        // viewport position after the next paint.
        nextTick(() => { if (isSentinelInView()) handleLoadMore(); });
    }, { immediate: true });

    const accumulated = computed(() => {
        const keys = Object.keys(pageBuckets.value).map(Number).sort((a, b) => a - b);
        const out = [];
        for (const k of keys) out.push(...pageBuckets.value[k]);
        return out;
    });

    // `total` drives the "N Results" label and the a11y count ONLY — never the
    // stop condition (see hasMore). It is the server's view of the whole list
    // and can disagree between cached responses; a slightly stale count reads
    // as a cosmetic wobble, whereas trusting it for control flow truncated the
    // list. `pages` is no longer read at all, for the same reason.
    const totalSpeakers = computed(() => parseInt(data.value?.total, 10) || 0);

    const results = computed(() => {
        if (!data.value) return '';
        return `${totalSpeakers.value} Results`;
    });

    // WHETHER MORE EXIST IS DECIDED BY THE PAGE WE JUST RECEIVED, not by a
    // `pages`/`total` carried across requests.
    //
    // 2026-09-16: /api/v2/speakers is cached per query, and its entries drifted
    // apart — production answered 302, 336 and 351 for the same catalog at the
    // same time. `pages` is ceil(total / per_page), so it disagreed too, and
    // whichever response landed last decided when to stop. Pages sliced against
    // different totals overlapped, and the grid gave up in the Ns with O-Z
    // unreachable.
    //
    // A short page means the end of the list, and that is a fact about the
    // response in hand rather than an arithmetic agreement between responses.
    // Cost when the catalog divides exactly by PER_PAGE: one extra request that
    // comes back empty and renders nothing.
    const hasMore = computed(() => {
        const cp = parseInt(loadingPage.value, 10) || 1;
        const bucket = pageBuckets.value[cp];
        // Still in flight. The watch above re-checks the sentinel once it lands.
        if (!bucket) return false;
        return bucket.length >= PER_PAGE;
    });

    // Cascading is true while we're backfilling earlier pages behind a deep-link,
    // so the IntersectionObserver can't advance loadingPage in the meantime.
    const cascading = ref((parseInt(page.value, 10) || 1) > 1);

    const handleLoadMore = () => {
        if (cascading.value || loadingMore.value || pending.value || !hasMore.value) return;
        loadingMore.value = true;
        loadingPage.value = (parseInt(loadingPage.value, 10) || 1) + 1;
    };

    // Backfill earlier pages behind an SSR'd deep-linked page. After backfill,
    // scroll to the first card of the target page so the user lands where they
    // expected.
    const backfillFromPage = async (target) => {
        if (target <= 1) return;
        cascading.value = true;
        for (let p = 1; p < target; p++) {
            if (pageBuckets.value[p]) continue;
            try {
                const result = await $fetch('/api/v2/speakers', { params: { ...params.value, page: p } });
                if (result?.speakers) {
                    pageBuckets.value = {
                        ...pageBuckets.value,
                        [p]: result.speakers.map(s => ({ ...s, _page: p })),
                    };
                }
            } catch (e) {
                console.error('[speaker-grid] backfill fetch failed for page', p, e);
            }
        }
        cascading.value = false;
        await nextTick();
        const firstCard = document.querySelector(`.speakers-grid li[data-page="${target}"]`);
        if (firstCard) {
            const y = firstCard.getBoundingClientRect().top + window.scrollY - 100;
            window.scrollTo({ top: y, behavior: 'auto' });
        }
    };

    // Track scroll to update URL page marker based on topmost visible card.
    let scrollTicking = false;
    const onScrollForUrl = () => {
        if (scrollTicking) return;
        scrollTicking = true;
        requestAnimationFrame(() => {
            scrollTicking = false;
            const offset = 120;
            const cards = document.querySelectorAll('.speakers-grid li[data-page]');
            let currentPage = 1;
            for (const li of cards) {
                const rect = li.getBoundingClientRect();
                if (rect.top >= offset - 10) break;
                const p = parseInt(li.getAttribute('data-page') || '1', 10);
                if (p > currentPage) currentPage = p;
            }
            if (currentPage === 1 && page.value !== 1) {
                page.value = 1;
            } else if (currentPage > 1 && parseInt(page.value, 10) !== currentPage) {
                page.value = currentPage;
            }
        });
    };

    watch([sessionTypes, themes], (newVals, oldVals) => {
        if (JSON.stringify(newVals) !== JSON.stringify(oldVals)) {
            pageBuckets.value = {};
            loadingPage.value = 1;
            page.value = 1;
            scrollIntoView(controls.value, { behavior: 'smooth', block: 'start' });
        }
    });

    const getTagNameById = (tagId) => {
        const all = data.value?.all_tags;
        if (!Array.isArray(all)) return '';
        const tag = all.find((t) => t.id === tagId);
        return tag ? tag.name : '';
    };

    const handleSearchClick = async () => {
        $clientPosthog?.capture('speakers_search_clicked');
        await navigateTo('/search?search=speakers');
    };

    const handleFilterDrawerToggle = () => {
        showTags.value = !showTags.value;
    };

    const sentinel = ref(null);

    const isSentinelInView = () => {
        const el = sentinel.value;
        if (!el) return false;
        const rect = el.getBoundingClientRect();
        const viewH = window.innerHeight;
        return rect.top < viewH + 400 && rect.bottom > -400;
    };

    // useIntersectionObserver reactively re-attaches when the sentinel ref changes,
    // which matters during client-side navigation where the sentinel may remount
    // as data populates. handleLoadMore's guards handle dedup.
    useIntersectionObserver(
        sentinel,
        ([entry]) => { if (entry?.isIntersecting) handleLoadMore(); },
        { rootMargin: '400px 0px 400px 0px' },
    );

    onMounted(() => {
        const targetPage = parseInt(page.value, 10) || 1;
        if (targetPage > 1) backfillFromPage(targetPage);
        window.addEventListener('scroll', onScrollForUrl, { passive: true });
    });

    onUnmounted(() => {
        if (typeof window !== 'undefined') window.removeEventListener('scroll', onScrollForUrl);
    });
</script>

<template>
    <div class="block-speakers-grid relative overflow-hidden text-white" :class="{ 'is-hero': treatAsHero }">
        <img src="/img/2026/speakers/grid-top-shard.png"
            srcset="/img/2026/speakers/grid-top-shard.png 1x, /img/2026/speakers/grid-top-shard@2x.png 2x"
            width="778" height="471"
            loading="lazy" decoding="async"
            alt="" aria-hidden="true"
            class="hero-shard hidden lg:display-block absolute top-0 right-0 w-[778px] h-auto pointer-events-none z-0" />

        <div class="relative z-[1] px-[var(--box-padding-lr-m)] md:px-[var(--box-padding-lr)]">
            <ProjectBreadcrumbs v-if="treatAsHero" light />

            <div class="text-white font-hubspot-sans font-light text-[16px] leading-[26px] tracking-[0.32px] mb-[30px] lg:mb-[60px]">
                <h1 v-html="headline" class="font-hubspot-serif font-normal mb-[1.125rem] text-[36px] leading-[1.1] lg:text-[72px]"></h1>
                <div v-html="copy" v-if="copy" class="max-w-[60rem] mb-0 copy"></div>
            </div>

            <div class="controls mb-[3.5rem]" ref="controls">
                <div class="flex flex-wrap items-center gap-[1.4375rem] mb-[1.3125rem] lg:mb-[2rem]">
                    <UButton
                        color="primary"
                        variant="outline"
                        icon="local-filter"
                        @click="handleFilterDrawerToggle"
                        class="more-filters"
                        :aria-expanded="showTags"
                        label="Show Speaker Filters">
                        Filters
                        <template v-if="activeFilterCount > 0">({{ activeFilterCount }})</template>
                    </UButton>
                    <ProjectSearchButton class="search-button" @click="handleSearchClick" />
                    <p
                        class="hidden lg:display-block text-[12px] text-white font-hubspot-sans font-normal uppercase tracking-[0.84px] leading-[1.5] whitespace-nowrap ml-auto"
                        v-if="data && totalSpeakers">
                        {{ results }}
                    </p>
                </div>

                <div v-if="data?.all_tags && activeFilterCount" class="mb-[2.5rem] lg:mb-[2rem]">
                    <ul class="flex flex-row flex-wrap gap-[1rem]">
                        <li v-for="tag in activeFilterIds" :key="tag">
                            <ProjectTag
                                :text="getTagNameById(tag)"
                                ariaText="Remove Tag"
                                colorSet="white"
                                :useHover="true"
                                :useX="true"
                                @click="removeFilter(tag)"
                                gtmEventName="speaker_filter_tag_clicked" />
                        </li>
                        <li class="clear-all">
                            <ProjectTag text="Clear All" colorSet="clear-all-white" :useHover="true" @click="clearFilters" />
                        </li>
                    </ul>
                </div>
            </div>

            <template v-if="accumulated.length">
                <ul class="speakers-grid grid gap-y-[60px] md:gap-y-[80px]
                    grid-cols-2 gap-x-[20px]
                    md:[grid-template-columns:repeat(auto-fill,223px)]
                    md:gap-x-[40px]
                    md:justify-between">
                    <li v-for="(speaker, index) in accumulated" :key="speaker.id" :data-page="speaker._page" class="md:w-[223px]">
                        <SpeakersGridCard :speaker="speaker" :index="index" theme="dark" :eager="speaker._page === 1" />
                    </li>
                </ul>
                <div ref="sentinel" aria-hidden="true" class="h-[1px]" />
                <div v-if="pending || loadingMore" class="flex flex-col items-center justify-center py-[40px]">
                    <FragmentsLoading :loading="pending || loadingMore" />
                </div>
            </template>
            <template v-else-if="pending">
                <div class="flex flex-col items-center justify-center min-h-[30vh]">
                    <FragmentsLoading :loading="pending" />
                </div>
            </template>
            <template v-else>
                <div class="empty flex flex-col items-center justify-center min-h-[40vh] text-[var(--cream)] text-center">
                    <h2 class="font-hubspot-serif text-[32px] mb-[1.375rem]">No speakers match your selections</h2>
                    <p class="mb-[2rem]">Try clearing your topic filters and searching again.</p>
                    <button
                        class="rounded-[50px] bg-transparent border border-[var(--cream)] text-[var(--cream)] px-5 py-2.5 font-hubspot-serif text-[17px] cursor-pointer hover:bg-[var(--cream)] hover:text-[var(--magenta)] transition-colors"
                        @click="tags = []">
                        Clear Filters
                    </button>
                </div>
            </template>

            <GlobalBackToTop />
        </div>

        <FragmentsDrawersAgendaFilters
            v-model:open="showTags"
            v-model:themes="themes"
            v-model:sessionTypes="sessionTypes"
            :objects="totalSpeakers"
            :year="year"
            basic-label="Filters"
            :show-advanced-filters="false"
            :show-session-toggles="false" />
    </div>
</template>

<style>
    .block-speakers-grid {
        padding: 43px 0 60px;
        background: linear-gradient(-43.11deg, var(--purple) 74.727%, var(--magenta) 96.807%);
        /* Stacking context only — no layer promotion. Previously we forced
           translateZ(0) here to stop the gradient flashing during the
           card pop animation, but that created a compositing layer that
           repaints during scroll and caused its own flicker. */
        isolation: isolate;
    }

    @media (min-width: 1024px) {
        .block-speakers-grid {
            padding: 97px 0 60px;
        }
    }

    .block-speakers-grid.is-hero {
        margin-top: calc(var(--header-height) * -1);
        padding-top: 97px;
    }

    @media (min-width: 1024px) {
        .block-speakers-grid.is-hero {
            padding-top: 150px;
        }
    }

    .block-speakers-grid .copy p {
        margin-bottom: 0.5rem;
    }

    /* Ultrawide column steps — raw media queries because Tailwind's arbitrary
       variants don't always generate grid-cols-N where N > 5 reliably. */
    @media (min-width: 1680px) {
        .block-speakers-grid .speakers-grid {
            grid-template-columns: repeat(6, minmax(0, 1fr));
        }
    }
    @media (min-width: 2048px) {
        .block-speakers-grid .speakers-grid {
            grid-template-columns: repeat(7, minmax(0, 1fr));
        }
    }
    @media (min-width: 2304px) {
        .block-speakers-grid .speakers-grid {
            grid-template-columns: repeat(8, minmax(0, 1fr));
        }
    }
    @media (min-width: 2560px) {
        .block-speakers-grid .speakers-grid {
            grid-template-columns: repeat(9, minmax(0, 1fr));
        }
    }

/* Delay the card-pop animation on the initial SSR batch so the first
       paint sits still for a beat before the cards animate in. Subsequent
       pages (added via infinite scroll) pop immediately. */
    .block-speakers-grid .speakers-grid > li[data-page="1"] .speakers-grid-card {
        animation-delay: 0s;
    }

    @media (prefers-reduced-motion: reduce) {
        .block-speakers-grid .speakers-grid > li[data-page="1"] .speakers-grid-card {
            animation-delay: 0s;
        }
    }

    /* Give the back-to-top wrapper its own height so the absolute button
       lands below the last grid row without overlapping it. */
    .block-speakers-grid .back-to-top {
        min-height: 36px;
        margin-top: 2.375rem;
    }

    .block-speakers-grid .back-to-top > button {
        left: var(--box-padding-lr-m);
    }

    @media (min-width: 1024px) {
        .block-speakers-grid .back-to-top > button {
            left: var(--box-padding-lr);
        }
    }

    .block-speakers-grid .back-to-top:not(.is-fixed):not(.offscreen) > button {
        left: 0;
        bottom: 0;
        transition-property: opacity, transform, background-color, color;
    }

    .block-speakers-grid .more-filters {
        color: white;
        border-color: white;
        border-width: 1px;
        border-radius: 50px;
        box-shadow: none;
        font-family: var(--font-hubspot-serif);
        font-size: calc(17 / var(--base-fs) * 1rem);
        line-height: 1.4;
        letter-spacing: 0.34px;
        padding: 10px 20px;
        gap: 10px;
    }

    .block-speakers-grid .more-filters:hover {
        background: white;
        color: var(--magenta);
        border-color: white;
    }

    .block-speakers-grid .search-button {
        color: white;
        border-color: white;
    }

    .block-speakers-grid .search-button:hover {
        background: white;
        color: var(--magenta);
        border-color: white;
    }

</style>
